import type { BannerStatus, SeedlingWizardRequest } from '@gcv/shared';
import { BannerType } from '@gcv/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { BanksApi } from 'api';
import { getFiDispensaryStore } from 'stores/FiDispensaryStore';
import {
  DISPENSARY_BANNER_STATUS_KEY,
  DISPENSARY_BUSINESS_STAGE,
  MINIFIED_DISPENSARIES_KEY
} from 'tanstack-hooks/keys';
import { useUser } from 'tanstack-hooks/user';

export function useUpdateBusinessStage(dispensaryId: string) {
  const { userBankId } = useUser();
  const banksApi = new BanksApi();
  const queryClient = useQueryClient();
  const dispensaryStore = getFiDispensaryStore();

  const { mutateAsync: updateBusinessStage } = useMutation({
    mutationKey: [DISPENSARY_BUSINESS_STAGE],
    mutationFn: (newData: SeedlingWizardRequest) =>
      banksApi.updateDispensaryStage(userBankId, dispensaryId, newData),
    onSuccess: (data, variables) => {
      queryClient.setQueryData([DISPENSARY_BUSINESS_STAGE, userBankId, dispensaryId], data);
      queryClient.setQueryData(
        [DISPENSARY_BANNER_STATUS_KEY, userBankId, dispensaryId],
        (oldData: BannerStatus[] | undefined) =>
          oldData?.filter((banner) => banner.type !== BannerType.SEEDLING_QUESTIONNAIRE_COMPLETED)
      );

      // Optimistically patch the MobX store so the accounts-table status column
      // reflects the new state immediately. The seedling-wizard endpoint returns
      // the new onboarding template result, so template_version is at the top
      // level of the response; template_result_id is left to refresh via
      // setCurrentDispensaryById, which pulls a fresh BankDispensary and feeds it
      // into setCurrentDispensaryTemplateInfo.
      dispensaryStore.updateDispensaryAssignedTemplate(dispensaryId, {
        newTemplateId: variables.new_template_id,
        newTemplateVersion: data.template_version,
        newStatus: variables.new_status
      });
      // Invalidate (don't optimistically patch) the React Query cache — patching
      // would leave template_result_id paired with a different template_id, and
      // any consumer that calls getCrbTemplateResult with that pair will fail.
      queryClient.invalidateQueries({ queryKey: [MINIFIED_DISPENSARIES_KEY, userBankId] });
    },
    meta: {
      snackbarSuccessMessage: 'Business stage has been updated successfully.',
      snackbarErrorMessage: 'An error has occurred updating business stage.  Please contact support.'
    }
  });
  return updateBusinessStage;
}
